import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscription/status/${sessionId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Error al verificar pago');
      }
      
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }
      
      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-[#1E3A5F] mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Verificando pago...</h1>
            <p className="text-slate-500">Por favor espera mientras confirmamos tu suscripción</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Pago exitoso!</h1>
            <p className="text-slate-500 mb-6">
              Tu suscripción PRO ha sido activada. Ahora tienes acceso a todas las funciones.
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-[#1E3A5F] hover:bg-[#162e4d]"
            >
              Ir al Dashboard
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Error en el pago</h1>
            <p className="text-slate-500 mb-6">
              No pudimos verificar tu pago. Por favor intenta de nuevo o contacta soporte.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/profile')}
                className="w-full bg-[#1E3A5F] hover:bg-[#162e4d]"
              >
                Intentar de nuevo
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Ir al Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
